import { IAudit, IAuditNode } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { assignIn } from 'lodash';
import moment from 'moment';
import { TreeArray } from 'src/app/common/tree-array/tree-array';
import { AuditNodeDto } from '../models/swodoc/audit-node-dto.model';
import { AuditService } from '../services/audit/audit.service';
import type { WoundChartData } from './woundhistory-chart.service';

export interface TherapyProgressData {
  woundChartData: WoundChartData;
  therapyImages: Array<{
    date: string;
    auditId: string;
    auditNodeId: string;
  }>;
}
export interface WoundData {
  length: number | undefined | null;
  width: number | undefined | null;
  depth: number | undefined | null;
  date: string;
}

@Injectable({
  providedIn: 'root',
})
export class TherapyprogressDataService {
  constructor(private _auditService: AuditService) {}

  public async getTherapyProgressData(audit: IAudit): Promise<TherapyProgressData> {
    const { patientId, therapyId, therapyTypeId, woundLocation } = audit;
    // TODO: maxAdditionalAudits from tenant config - currently 4 for MWM
    const audits = await this.retreiveAffectedAudits(patientId, therapyId, therapyTypeId, woundLocation, 4);
    if (this.shouldAddCurrentAudit(audit)) {
      audits.push(audit);
    }
    const auditsWithImages = audits.filter((auditItem: IAudit) => this.getImageNode(auditItem.auditNodes));

    return {
      woundChartData: await this.getWoundChartData(audits),
      therapyImages: auditsWithImages.map((auditItem: IAudit) => ({
        date: auditItem.createdUtc,
        auditId: auditItem._id,
        auditNodeId: (this.getImageNode(auditItem.auditNodes) as any)?._id,
      })),
    };
  }

  public async getWoundChartData(audits: IAudit[]): Promise<WoundChartData> {
    return this.mapAuditsToRawChartData(audits);
  }

  private mapAuditsToRawChartData(audits: IAudit[]): WoundChartData {
    const woundData: WoundData[] = audits
      .map((audit: IAudit) => {
        const treeArray = assignIn(new TreeArray<AuditNodeDto>(), audit.auditNodes);
        const width = this.getWithNode(audit.auditNodes)?.responses[0]?.value?.toString()?.replace(',', '.');
        const length = this.getLengthNode(audit.auditNodes)?.responses[0]?.value?.toString()?.replace(',', '.');
        const depth = this.getDepthNode(audit.auditNodes)?.responses[0]?.value?.toString()?.replace(',', '.');
        const createdAt =
          treeArray.findRec(node => node.globalType === '171')?.responses[0]?.value?.toString() || audit.createdAt;
        return {
          date: moment(createdAt),
          width: parseFloat(width) || 0,
          length: parseFloat(length) || 0,
          depth: parseFloat(depth) || 0,
        };
      })
      .sort((a, b) => a.date.unix() - b.date.unix())
      .map(item => ({ ...item, date: item.date.format('DD.MM.YYYY') }));
    return {
      labels: woundData.map(data => data.date),
      lengthData: woundData.map(data => data.length),
      widthData: woundData.map(data => data.width),
      depthData: woundData.map(data => data.depth),
    };
  }

  private async retreiveAffectedAudits(
    patientId: string,
    therapyId: number,
    therapyTypeId: number,
    woundLocation: string,
    maxAdditionalAudits?: number | undefined
  ): Promise<IAudit[]> {
    let affectedAudits = await this._auditService.getNotArchivedAuditsForTherapyProgress({
      patientId,
      therapyId,
      therapyTypeId,
      woundLocation,
      completed: true,
    });
    // filter out audits without length/width
    affectedAudits = affectedAudits.filter((audit: IAudit) => {
      return this.shouldAddCurrentAudit(audit);
    });
    if (maxAdditionalAudits && affectedAudits.length > maxAdditionalAudits) {
      const sortedAudits = affectedAudits
        .map((audit: IAudit) => {
          const createdAt =
            assignIn(new TreeArray<AuditNodeDto>(), audit.auditNodes)
              .findRec(node => node.globalType === '171')
              ?.responses[0]?.value?.toString() || audit.createdAt;
          return {
            ...audit,
            dateForSort: createdAt,
          };
        })
        .sort((a, b) => moment(a.dateForSort).unix() - moment(b.dateForSort).unix());
      const firstAudit = sortedAudits.shift();
      const additionalAudits = sortedAudits.slice((maxAdditionalAudits - 1) * -1);
      return [firstAudit, ...additionalAudits];
    }
    return affectedAudits;
  }

  private getImageNode(auditNodes: IAuditNode[]): IAuditNode {
    return assignIn(new TreeArray<AuditNodeDto>(), auditNodes).findRec(node => node.globalType === '178');
  }

  private getWithNode(auditNodes: IAuditNode[]): IAuditNode {
    return assignIn(new TreeArray<AuditNodeDto>(), auditNodes).findRec(node => node.globalType === '148');
  }

  private getLengthNode(auditNodes: IAuditNode[]): IAuditNode {
    return assignIn(new TreeArray<AuditNodeDto>(), auditNodes).findRec(node => node.globalType === '149');
  }

  private getDepthNode(auditNodes: IAuditNode[]): IAuditNode {
    return assignIn(new TreeArray<AuditNodeDto>(), auditNodes).findRec(node => node.globalType === '150');
  }

  private shouldAddCurrentAudit(audit: IAudit): boolean {
    return (
      !!this.getImageNode(audit.auditNodes) ||
      !!this.getWithNode(audit.auditNodes) ||
      !!this.getLengthNode(audit.auditNodes) ||
      !!this.getDepthNode(audit.auditNodes)
    );
  }
}
